import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { Transaction } from "../../../../api/src/transactions/lib";
import type { TransactionsController } from "../../../../api/src/transactions/transactions.controller";
import TransactionsTable, {
  useAllTransactions,
  type TransactionsExportState,
} from "../../components/TransactionsTable";
import { useAuthenticatedQuery } from "../../lib/api";
import { processFileResponse } from "../../lib/file";
import { HouseholdContext } from "./HouseholdInfo";

/**
 * The household's transactions, from every page of
 * `GET /v1/transactions/household/:id` (APP-1652). The export
 * (`/transactions/household/:id/export`) is not admitted and stays
 * unversioned.
 */
export function useQueryHouseholdTransactions(householdId: number) {
  return useAllTransactions(`/v1/transactions/household/${householdId}`);
}

/**
 * `/transactions/household/:id/export` with the state of the table (APP-1771): the
 * sweep switch always, and each column filter only with a value. Type and Sub
 * Type go as labels, repeated for each one. The tab has no Household column,
 * no dates and no tick box, so none of those is sent.
 */
export function householdTransactionsExportUrl(
  householdId: number,
  { excludeSweep, filters }: TransactionsExportState,
) {
  const params = new URLSearchParams({ "exclude-sweep": String(excludeSweep) });
  filters.type.forEach((label) => params.append("type", label));
  filters.subType.forEach((label) => params.append("sub-type", label));
  if (filters.symbol) params.append("symbol", filters.symbol);
  if (filters.description) params.append("description", filters.description);
  return `/transactions/household/${householdId}/export?${params.toString()}`;
}

export function useQueryExportHouseholdTransactions(householdId: number) {
  // Read when the request is built, so the key (and the cache) stays one.
  const url = useRef(
    householdTransactionsExportUrl(householdId, {
      excludeSweep: false,
      filters: { type: [], subType: [] },
    }),
  );
  const query = useAuthenticatedQuery<
    UnpackResponse<TransactionsController["exportHouseholdTransactions"]>,
    void,
    [string, string, number, string]
  >({
    queryKey: ["transactions", "household", householdId, "export"],
    urlBuilderFn: () => url.current,
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
  const { refetch } = query;
  const exportWith = useCallback(
    (state: TransactionsExportState) => {
      url.current = householdTransactionsExportUrl(householdId, state);
      return refetch();
    },
    [householdId, refetch],
  );
  return { query, exportWith };
}

type TransactionRow = Transaction;

/**
 * Remounts the list per household, so the rows the page walk collected for one
 * household are never shown under another (`lib/pagination.ts`).
 */
const HouseholdTransactions = () => {
  const { household } = useContext(HouseholdContext);
  const householdId = household?.id ?? -1;

  return (
    <HouseholdTransactionsOfHousehold
      key={householdId}
      householdId={householdId}
    />
  );
};

const HouseholdTransactionsOfHousehold = ({
  householdId,
}: {
  householdId: number;
}) => {
  const columnHelper = useMemo(() => createColumnHelper<TransactionRow>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("accountNumber", {
        cell: (info) => (
          <Link to={`/clients/accounts/${info.row.original.accountId}`}>
            {info.getValue()}
          </Link>
        ),
        header: () => "Account Number",
        enableColumnFilter: false,
      }),
    ],
    [columnHelper],
  );

  const {
    isPending: isPendingTransactions,
    isError: isErrorTransactions,
    data: transactions,
  } = useQueryHouseholdTransactions(householdId);

  const { query: queryExport, exportWith } =
    useQueryExportHouseholdTransactions(householdId);

  return (
    <TransactionsTable
      additionalColumns={columns}
      isLoading={isPendingTransactions}
      isError={isErrorTransactions}
      transactions={transactions ?? []}
      queryExport={queryExport}
      onExport={exportWith}
    />
  );
};

export default HouseholdTransactions;
